import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs/internal/Subscription';
import { proFormQuestionTypes } from 'src/app/common/utilities/proform-helpers';
import { ProformViewStates } from '../../../../dtos/proforms.dto';
import { ProformsAdminService } from '../../../../services/proforms/proforms-admin.service';
import {
  ProformFieldTypes,
  ProFormsAPIResponse,
  ProFormsQuestionAPIResponse,
} from '../../../../types/responses/proforms.responses';
import { ModalComponent } from '../../../modals/modal/modal.component';
import { ProformsAdminQuestionAnswerCheckboxGridComponent } from '../proforms-admin-question-answer-checkbox-grid/proforms-admin-question-answer-checkbox-grid.component';
import { ProformsAdminQuestionAnswerOptionsComponent } from '../proforms-admin-question-answer-options/proforms-admin-question-answer-options.component';

@Component({
  selector: 'app-proforms-admin-question',
  standalone: false,

  templateUrl: './proforms-admin-question.component.html',
  styleUrl: './proforms-admin-question.component.scss',
})
export class ProformsAdminQuestionComponent implements OnInit {
  @Input() question: ProFormsQuestionAPIResponse;

  @ViewChild('typeChangeConfirmModal') typeChangeConfirmModal: ModalComponent;

  @ViewChild('answerOptionsComponent')
  answerOptionsComponent: ProformsAdminQuestionAnswerOptionsComponent;

  @ViewChild('checkboxGridComponent')
  checkboxGridComponent: ProformsAdminQuestionAnswerCheckboxGridComponent;

  subs: Subscription[] = [];

  form: ProFormsAPIResponse;

  editMode: boolean;

  initialQuestionContent: string;

  initialDescriptionContent: string;

  initialFieldType: ProformFieldTypes;

  selectedFieldType: ProformFieldTypes;

  pendingFieldType: ProformFieldTypes | null = null;

  isContentBlockChange = false;

  proFormQuestionTypes = proFormQuestionTypes;

  ProformFieldTypes = ProformFieldTypes;

  viewStates = ProformViewStates;

  constructor(
    private proformsAdminService: ProformsAdminService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.editMode = false;

    this.subs.push(
      this.proformsAdminService.currentActiveQuestion.subscribe((response) => {
        if (this.question?.id === response) {
          this.editMode = true;
          this.initialQuestionContent = this.question.question;
          this.initialDescriptionContent = this.question.helper_text;
          this.initialFieldType = this.question.field_type;
          this.selectedFieldType = this.question.field_type;
        } else {
          this.editMode = false;
        }
      })
    );

    this.subs.push(
      this.proformsAdminService.currentProForm.subscribe((response) => {
        if (response) {
          this.form = response;
        }
      })
    );
  }

  handleCardClick(question: ProFormsQuestionAPIResponse) {
    if (!this.editMode && question) {
      this.proformsAdminService.setActiveQuestion(question.id);
    }
  }

  handleTypeChange() {
    const prev = this.initialFieldType;
    const next = this.selectedFieldType;

    const optionTypes = [
      ProformFieldTypes.CHECKBOX,
      ProformFieldTypes.DROPDOWN,
      ProformFieldTypes.RADIO,
      ProformFieldTypes.SCALE,
      ProformFieldTypes.CHECKBOX_GRID,
    ];

    const isSwitchingBetweenCheckboxToSingleSelect =
      (prev === ProformFieldTypes.CHECKBOX &&
        next === ProformFieldTypes.RADIO) ||
      (prev === ProformFieldTypes.CHECKBOX &&
        next === ProformFieldTypes.DROPDOWN);

    const isSwitchingBetweenSingleSelectToCheckbox =
      (prev === ProformFieldTypes.RADIO &&
        next === ProformFieldTypes.CHECKBOX) ||
      (prev === ProformFieldTypes.DROPDOWN &&
        next === ProformFieldTypes.CHECKBOX);

    const isLeavingOptionType = optionTypes.includes(prev);
    const isChangingToDifferentType = prev !== next;

    // Check if leaving content block with custom content
    const isLeavingContentBlockWithContent =
      prev === ProformFieldTypes.CONTENT_BLOCK &&
      next !== ProformFieldTypes.CONTENT_BLOCK &&
      this.question.question !== 'Content';

    const shouldTriggerWarning =
      (isLeavingOptionType &&
        !isSwitchingBetweenCheckboxToSingleSelect &&
        !isSwitchingBetweenSingleSelectToCheckbox &&
        isChangingToDifferentType) ||
      isLeavingContentBlockWithContent;

    const hasOptions =
      !!this.answerOptionsComponent?.question.answerOptions.length ||
      !!this.checkboxGridComponent?.question.answerOptions.length;

    if (
      shouldTriggerWarning &&
      (hasOptions || isLeavingContentBlockWithContent)
    ) {
      this.question.field_type = prev;
      setTimeout(() => {
        this.selectedFieldType = prev;
      });

      this.isContentBlockChange = isLeavingContentBlockWithContent;
      this.openConfirmModal(next);
    } else {
      this.applyFieldTypeChange(next, isSwitchingBetweenCheckboxToSingleSelect);
    }
  }

  applyFieldTypeChange(newValue: ProformFieldTypes, checkboxToRadio = false) {
    if (
      this.initialFieldType === ProformFieldTypes.CONTENT_BLOCK &&
      newValue !== ProformFieldTypes.CONTENT_BLOCK
    ) {
      this.updateLocalQuestionTitle('Question');
    } else if (
      this.initialFieldType !== ProformFieldTypes.CONTENT_BLOCK &&
      newValue === ProformFieldTypes.CONTENT_BLOCK
    ) {
      this.updateLocalQuestionTitle('Content');
    }

    this.question.field_type = newValue;
    this.initialFieldType = newValue;
    this.selectedFieldType = newValue;

    if (checkboxToRadio) {
      const hasMultipleCorrectAnswers =
        this.answerOptionsComponent.question.answerOptions.filter(
          (option) => option.is_correct === 1
        ).length > 1;

      if (hasMultipleCorrectAnswers) {
        this.answerOptionsComponent.question.answerOptions.forEach((opt) => {
          this.proformsAdminService
            .updateCorrectAnswerOption(opt.id, {
              is_correct: 0,
            })
            .subscribe();
        });
      }
    }

    this.proformsAdminService.updateQuestion(
      this.question.id,
      this.question.proform_form_section_id,
      { field_type: newValue }
    );
  }

  openConfirmModal(newValue: ProformFieldTypes) {
    this.pendingFieldType = newValue;
    this.typeChangeConfirmModal.open();
  }

  confirmTypeChange() {
    if (this.pendingFieldType) {
      this.applyFieldTypeChange(this.pendingFieldType);
      this.selectedFieldType = this.pendingFieldType;
      this.pendingFieldType = null;
    }
    this.isContentBlockChange = false;
    this.typeChangeConfirmModal.close();
  }

  deleteQuestion(questionId: number, sectionId: number) {
    this.proformsAdminService.deleteQuestion(questionId, sectionId);
  }

  copyQuestion(questionId: number, sectionId: number) {
    this.proformsAdminService.copyQuestion(questionId, sectionId);
  }

  updateLocalQuestionTitle(newQuestion: string) {
    this.initialQuestionContent = newQuestion;
    this.updateQuestionTitle();
  }

  updateQuestionTitle() {
    if (
      this.question &&
      this.question.question !== this.initialQuestionContent
    ) {
      this.proformsAdminService.updateQuestion(
        this.question.id,
        this.question.proform_form_section_id,
        {
          question: this.initialQuestionContent,
        }
      );
    }
  }

  updateLocalDescription(newDescription: string) {
    this.initialDescriptionContent = newDescription;
    this.updateDescription();
  }

  updateDescription() {
    if (this.question.helper_text !== this.initialDescriptionContent) {
      this.proformsAdminService.updateQuestion(
        this.question.id,
        this.question.proform_form_section_id,
        {
          helper_text: this.initialDescriptionContent,
        }
      );
    }
  }

  toggleRequired($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    this.question.required = inputElement.checked ? 1 : 0;
    this.proformsAdminService.updateQuestion(
      this.question.id,
      this.question.proform_form_section_id,
      {
        required: this.question.required,
      }
    );
  }
}
